import { Activity, Box, Eye, FileBox, FileText, Network } from "lucide-react";
import { type MouseEvent, type ReactNode } from "react";
import { Link } from "react-router";

import { entityIdFromPath, type EntityLink, type EntityLinkType } from "~/ai-chat/model/entity-link";
import { type FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import { type MeshViewRequest, viewableMeshFormat } from "~/ai-chat/model/mesh-file-types";
import { ChatCopyButton } from "~/ai-chat/ui/chat-copy-button";

interface ChatEntityChipProps {
  link: EntityLink;
  children: ReactNode;
  // Open the mesh in the inline viewer (rendered by the enclosing message). Absent
  // when the chip isn't inside a message that can host a viewer.
  onViewMesh?: (mesh: MeshViewRequest) => void;
  // Open a non-mesh file in the inline preview (rendered by the enclosing
  // message). Absent when the chip isn't inside a message that can host one.
  onPreview?: (file: FilePreviewRequest) => void;
}

const ENTITY_ICONS: Record<EntityLinkType, typeof Network> = {
  system: Network,
  model: FileText,
  artifact: FileBox,
  job: Activity,
};

export function ChatEntityChip({ link, children, onViewMesh, onPreview }: ChatEntityChipProps) {
  const Icon = ENTITY_ICONS[link.type];
  const entityId = entityIdFromPath(link.path);

  // A file chip (model/artifact) carries the file name as its text. Meshes get a
  // View 3D button; any other file gets a Preview button (the two are exclusive).
  const fileName = typeof children === "string" ? children : "";
  const isFileChip = link.type === "model" || link.type === "artifact";
  const meshFormat = fileName ? viewableMeshFormat(fileName) : undefined;
  const canView3d = Boolean(meshFormat && entityId && onViewMesh && isFileChip);
  const canPreview = Boolean(!meshFormat && entityId && onPreview && isFileChip && fileName);

  // The copy button sits beside the Link (never inside it — nested interactive elements are
  // invalid and steal the anchor's clicks), so the pill wrapper carries the shared styling.
  return (
    <span className="bg-background/60 text-foreground hover:bg-background inline-flex max-w-full items-baseline gap-1 rounded-full pr-1.5 align-baseline text-xs font-medium shadow-[inset_0_0_0_1px_var(--border)] transition-[background-color] duration-150">
      <Link
        to={link.path}
        onClick={(event: MouseEvent<HTMLAnchorElement>) => focusMainWindowIfExternal(event.currentTarget)}
        className="inline-flex min-w-0 items-baseline gap-1 py-0.5 pl-2 transition-[scale] duration-150 active:scale-[0.96]"
      >
        <Icon className="text-muted-foreground size-3 shrink-0 self-center" aria-hidden="true" />
        <span className="min-w-0 truncate">{children}</span>
      </Link>
      {canView3d && meshFormat && entityId ? (
        <button
          type="button"
          onClick={() => onViewMesh?.({ resourceId: entityId, name: fileName, format: meshFormat })}
          aria-label={`View ${fileName} in 3D`}
          className="text-muted-foreground hover:text-foreground shrink-0 self-center rounded p-1 transition-colors"
        >
          <Box className="size-3" aria-hidden="true" />
        </button>
      ) : null}
      {canPreview && entityId ? (
        <button
          type="button"
          onClick={() => onPreview?.({ resourceId: entityId, name: fileName })}
          aria-label={`Preview ${fileName}`}
          className="text-muted-foreground hover:text-foreground shrink-0 self-center rounded p-1 transition-colors"
        >
          <Eye className="size-3" aria-hidden="true" />
        </button>
      ) : null}
      {entityId ? (
        <ChatCopyButton
          text={entityId}
          copiedMessage="ID copied."
          ariaLabel={`Copy ${link.type} ID`}
          className="self-center rounded p-1"
        />
      ) : null}
    </span>
  );
}

// A popped-out chat renders through a portal into an external window (Document PiP or
// window.open), while navigation still happens on the main window's router — without this,
// clicking a chip in the popout changes a window the user may not be looking at and reads
// as a dead link. No-op in the docked chat, where the chip's document is the main window's.
export function focusMainWindowIfExternal(node: Element): void {
  const clickedIn = node.ownerDocument.defaultView;
  if (clickedIn && clickedIn !== window) {
    window.focus();
  }
}
